import { watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import type { LocationQuery, RouteLocationRaw } from 'vue-router'

import { queryCallbackHandlers, type QueryCallbackOutcome } from './queryCallbackRegistry'

// The `?param` delegation watcher (contract 05 §8, ORP-18611). Mounted EXACTLY ONCE, in
// root App.vue: every matching handler runs in registration order, the first redirect short-circuits
// the rest (v3's `return next(...)`), and the consumed params are stripped in ONE `replace` —
// never a push, which would leave a back button that re-fires the callback.

/**
 * Credentials that must leave the URL even though nothing consumes them (contract 06 §16 — "no
 * credential ever reaches a URL"; ORP-20413).
 *
 * The seam strips a param only once a HANDLER consumed it, and v4 deliberately registers no
 * `token` handler — `registerAuthQueryCallback` owns `handoff` alone, because exchanging a bearer
 * out of a query string is the thing the auth contract forbids. Not consuming it, though, left
 * nothing to remove it: the backend's Shopify connect return
 * (`oauth/views.py:1305-1307`) redirects with `connect=shopify&token=<bearer>`, `connect` gets
 * handled and stripped, and the bearer survives in the address bar, history and any URL shared
 * from that page. Declining to read a credential and leaving it on screen are different things.
 *
 * `token` ONLY. `code` and `state` are overloaded here — `code=SHOPIFY_STORE_CONNECTED` is a
 * message code, and integrations' `shop` handler reads `code` to REJECT an unsafe payload — so
 * stripping them would change behaviour rather than protect anything.
 */
const URL_CREDENTIAL_PARAMS: readonly string[] = ['token']

/**
 * `/callback/*` is excluded: those routes own their own payload inspection —
 * `ShopifyCallbackView.vue` and `OAuthCallbackView.vue` both read `token` precisely to refuse a
 * callback that carries one — and every handler returns `{preserve:true}` there anyway. Stripping
 * underneath them would turn that refusal into a silent accept.
 */
function isDedicatedCallbackPath(path: string): boolean {
  return path.startsWith('/callback/')
}

/** The credential params present on a snapshot that the seam must remove regardless of handlers. */
function urlCredentials(snapshot: QuerySnapshot): string[] {
  if (isDedicatedCallbackPath(snapshot.path)) return []
  return URL_CREDENTIAL_PARAMS.filter((param) => snapshot.query[param] !== undefined)
}

/** The triggering route. Handlers see the query as it was when their param arrived, not as it is now. */
interface QuerySnapshot {
  path: string
  hash: string
  query: LocationQuery
  versions: ReadonlyMap<string, number>
}

interface PendingRedirect {
  outcome: QueryCallbackOutcome
  owner: string
  ownerVersion: number
  path: string
}

/** vue-router yields `string | null` (`?p` → null) and `string[]` when repeated; handlers get one string. */
function firstValue(raw: LocationQuery[string]): string {
  const value = Array.isArray(raw) ? raw[0] : raw
  return value ?? ''
}

/**
 * Compares two query values BY VALUE. A repeated param (`?p=1&p=2`) parses to a fresh array on
 * every navigation, so `!==` would identity-compare and call an untouched param "changed" —
 * re-firing its handler for a value it already handled (§22). Absent (`undefined`) never equals
 * present, which is what makes a mid-flight strip of a param the user dropped stay skipped.
 */
function sameValue(
  a: LocationQuery[string] | undefined,
  b: LocationQuery[string] | undefined,
): boolean {
  if (!Array.isArray(a) && !Array.isArray(b)) return a === b
  const left = Array.isArray(a) ? a : [a]
  const right = Array.isArray(b) ? b : [b]
  return left.length === right.length && left.every((value, index) => value === right[index])
}

function cloneQuery(query: LocationQuery): LocationQuery {
  const copy: LocationQuery = {}
  for (const param of Object.keys(query)) {
    const value = query[param]
    if (value === undefined) continue
    copy[param] = Array.isArray(value) ? [...value] : value
  }
  return copy
}

function strip(from: QuerySnapshot, consumed: string[]): RouteLocationRaw {
  const query = { ...from.query }
  for (const param of consumed) delete query[param]
  return { path: from.path, hash: from.hash, query }
}

export function useQueryCallbacks(): void {
  const route = useRoute()
  const router = useRouter()

  // A version identifies one arrival of one param. Companion-query changes keep the version;
  // value/presence/path changes advance it. This lets the drain coalesce to the latest URL while
  // still distinguishing remove→re-add of the same textual value as a new arrival.
  const versions = new Map<string, number>()
  const handledVersions = new Map<string, number>()
  const preservedVersions = new Map<string, number>()
  let observed: Pick<QuerySnapshot, 'path' | 'query'> | undefined
  let running = false
  let pending: QuerySnapshot | undefined

  function captureSnapshot(): QuerySnapshot {
    const current = router.currentRoute.value
    const query = cloneQuery(current.query)

    for (const [param] of queryCallbackHandlers()) {
      const changed =
        !observed ||
        !versions.has(param) ||
        observed.path !== current.path ||
        !sameValue(observed.query[param], query[param])
      if (changed) versions.set(param, (versions.get(param) ?? 0) + 1)
    }

    observed = { path: current.path, query }
    return {
      path: current.path,
      hash: current.hash,
      query,
      versions: new Map(versions),
    }
  }

  function hasUnhandled(snapshot: QuerySnapshot): boolean {
    for (const [param] of queryCallbackHandlers()) {
      const version = snapshot.versions.get(param)
      if (
        snapshot.query[param] !== undefined &&
        version !== undefined &&
        handledVersions.get(param) !== version &&
        preservedVersions.get(param) !== version
      ) {
        return true
      }
    }
    return false
  }

  function handledParams(snapshot: QuerySnapshot): string[] {
    const handled: string[] = []
    for (const [param] of queryCallbackHandlers()) {
      const version = snapshot.versions.get(param)
      if (
        snapshot.query[param] !== undefined &&
        version !== undefined &&
        handledVersions.get(param) === version
      ) {
        handled.push(param)
      }
    }
    return handled
  }

  function redirectIsCurrent(redirect: PendingRedirect, snapshot: QuerySnapshot): boolean {
    return (
      snapshot.path === redirect.path &&
      snapshot.versions.get(redirect.owner) === redirect.ownerVersion &&
      snapshot.query[redirect.owner] !== undefined
    )
  }

  async function runOnce(snapshot: QuerySnapshot): Promise<PendingRedirect | undefined> {
    for (const [param, handler] of queryCallbackHandlers()) {
      const raw = snapshot.query[param]
      const version = snapshot.versions.get(param)
      if (raw === undefined || version === undefined || handledVersions.get(param) === version) {
        continue
      }

      // A previous handler may have awaited while a newer snapshot arrived. An unchanged sibling
      // still belongs to this triggering query; a moved or changed one belongs to the latest drain.
      if (pending?.path !== undefined && pending.path !== snapshot.path) break
      if (pending && pending.versions.get(param) !== version) continue

      // Invocation itself consumes this generation even if the feature rejects; errors are feature-
      // reported and must not wedge the URL or replay the side effect.
      handledVersions.set(param, version)
      try {
        // The owning feature reports its own failure; the seam only guarantees the param is
        // stripped either way (contract §8), so a rejected handler must not abort the strip.
        const result = await handler({
          path: snapshot.path,
          value: firstValue(raw),
          query: snapshot.query,
        })
        if (result && 'preserve' in result) {
          handledVersions.delete(param)
          preservedVersions.set(param, version)
          continue
        }
        if (result) {
          return { outcome: result, owner: param, ownerVersion: version, path: snapshot.path }
        }
      } catch {
        // Intentionally swallowed — see above.
      }
    }
    return undefined
  }

  async function consume(snapshot: QuerySnapshot): Promise<void> {
    if (running) {
      pending = snapshot
      return
    }
    running = true
    let next: QuerySnapshot | undefined = snapshot
    try {
      while (next) {
        pending = undefined
        const redirect = await runOnce(next)
        const latest: QuerySnapshot = pending ?? next

        // Registration order is load-bearing: a still-current redirect remains the first branch
        // that wins, even if a sibling changed while it awaited. A changed/removed owner makes the
        // redirect stale; its new generation is handled below against the complete latest query.
        if (redirect && redirectIsCurrent(redirect, latest)) {
          await router.replace(redirect.outcome.redirect)
          next = pending
          continue
        }

        // Never strip between generations. Processing the complete latest snapshot first preserves
        // companion params in QueryCallbackContext and lets unchanged handlers stay exactly-once.
        if (hasUnhandled(latest)) {
          next = latest
          continue
        }

        // Unowned credentials ride out on the same replace as the handled params. Listed second so
        // a param that IS handled is not duplicated by the filter below.
        const handled = handledParams(latest)
        const consumed = [...handled, ...urlCredentials(latest).filter((p) => !handled.includes(p))]
        if (consumed.length === 0) break

        const current = router.currentRoute.value
        if (current.path !== latest.path) {
          next = pending
          continue
        }
        await router.replace(
          strip(
            {
              path: current.path,
              hash: current.hash,
              query: current.query,
              versions: latest.versions,
            },
            consumed,
          ),
        )
        next = pending
      }
    } finally {
      running = false
      const queued = pending
      pending = undefined
      if (queued) void consume(queued)
    }
  }

  watch(
    () => [route.path, route.query] as const,
    () => {
      void consume(captureSnapshot())
    },
    { immediate: true, flush: 'sync' },
  )
}
